import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import type {
  CreateSnapshotRequest,
  Snapshot,
  SnapshotStatusResponse,
  UpdateSnapshotRequest,
} from '@/lib/types';

/** Reads the org's devbox snapshot list, newest first (server-sorted). */
export function useSnapshots(options?: { enabled?: boolean }) {
  const orgKey = useOrgKey();
  return useQuery({
    queryKey: queryKeys.snapshots(orgKey),
    queryFn: api.listSnapshots,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Polls a snapshot's async capture status every 5s while it is
 * `in_progress` (the blueprint building-poll pattern). Once the operation
 * settles, the list is invalidated so the new snapshot's final state
 * (e.g. `sizeBytes`) shows up without a manual refresh.
 */
export function useSnapshotStatus(id: string | null | undefined, options?: { enabled?: boolean }) {
  const orgKey = useOrgKey();
  const qc = useQueryClient();
  return useQuery({
    queryKey: queryKeys.snapshotStatus(orgKey, id ?? 'none'),
    queryFn: async () => {
      const status = await api.getSnapshotStatus(id!);
      if (status.status !== 'in_progress') {
        void qc.invalidateQueries({ queryKey: queryKeys.snapshots(orgKey) });
      }
      return status;
    },
    enabled: (options?.enabled ?? true) && Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data as SnapshotStatusResponse | undefined;
      return !data || data.status === 'in_progress' ? 5000 : false;
    },
  });
}

export function useCreateSnapshot() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ agentId, data }: { agentId: string; data: CreateSnapshotRequest }) =>
      api.createAgentSnapshot(agentId, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.snapshots(orgKey) });
    },
  });
}

export function useUpdateSnapshot() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSnapshotRequest }) =>
      api.updateSnapshot(id, data),
    onSuccess: (updated) => {
      qc.setQueryData<Snapshot[]>(
        queryKeys.snapshots(orgKey),
        (old) => old?.map((s) => (s.id === updated.id ? updated : s)) ?? [updated],
      );
    },
  });
}

export function useDeleteSnapshot() {
  const qc = useQueryClient();
  const orgKey = useOrgKey();
  return useMutation({
    mutationFn: (id: string) => api.deleteSnapshot(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<Snapshot[]>(
        queryKeys.snapshots(orgKey),
        (old) => old?.filter((s) => s.id !== id) ?? [],
      );
    },
  });
}
